"use client";

import Button from "@/components/Button";
import Text from "@/components/Text";
import classNames from "@/helpers/classNames";
import styles from "./styles.module.scss";

export interface CartSubtotalProps {
  subtotal: number;
  shipping?: number;
  taxes?: number;
  total: number;
  /**
   * When true, GST is baked into `subtotal` and `shipping` already — the
   * "Taxes" row is hidden and an "Includes GST $X" sub-row is shown under
   * the total instead (Australian shelf-price convention). When false,
   * "Taxes" is shown as its own line and added on top (US/UK convention).
   */
  isTaxInclusive?: boolean;
  onCheckout?: () => void;
  checkoutLabel?: string;
  isLoading?: boolean;
  className?: string;
}

const formatPrice = (amount: number) => `$${amount.toFixed(2)}`;

const CartSubtotal = ({
  subtotal,
  shipping = 0,
  taxes = 0,
  total,
  isTaxInclusive = false,
  onCheckout,
  checkoutLabel = "Proceed to checkout",
  isLoading = false,
  className,
}: CartSubtotalProps) => (
  <div className={classNames(styles.container, className)}>
    <div className={styles.rows}>
      <div className={styles.row}>
        <Text as="p" text="Subtotal:" variant="label" size="md" className={styles.rowLabel} />
        <Text
          as="p"
          text={formatPrice(subtotal)}
          variant="label"
          size="md"
          weight="medium"
          className={styles.rowValue}
        />
      </div>
      <div className={styles.row}>
        <Text as="p" text="Shipping:" variant="label" size="md" className={styles.rowLabel} />
        <Text
          as="p"
          text={formatPrice(shipping)}
          variant="label"
          size="md"
          weight="medium"
          className={styles.rowValue}
        />
      </div>
      {!isTaxInclusive && (
        <div className={styles.row}>
          <Text as="p" text="Taxes:" variant="label" size="md" className={styles.rowLabel} />
          <Text
            as="p"
            text={formatPrice(taxes)}
            variant="label"
            size="md"
            weight="medium"
            className={styles.rowValue}
          />
        </div>
      )}
    </div>

    <div>
      <div className={styles.totalRow}>
        <Text as="p" text="Total:" size="md" weight="semibold" className={styles.totalLabel} />
        <Text
          as="p"
          text={formatPrice(total)}
          size="md"
          weight="semibold"
          className={styles.totalValue}
        />
      </div>
      {isTaxInclusive && taxes > 0 && (
        <div className={styles.inclTax}>
          <Text
            as="p"
            text="Includes GST:"
            variant="label"
            size="sm"
            className={styles.inclTaxLabel}
          />
          <Text
            as="p"
            text={formatPrice(taxes)}
            variant="label"
            size="sm"
            className={styles.inclTaxValue}
          />
        </div>
      )}
    </div>

    {onCheckout && (
      <Button
        text={checkoutLabel}
        theme="yellow"
        size="lg"
        onClick={onCheckout}
        disabled={isLoading}
        className={styles.checkoutButton}
      />
    )}
  </div>
);

export default CartSubtotal;
